import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SvgIcon } from '../../../../components';
import { getStateCode } from '../../../../util/stateNames';
import { resolveLinkupAttendeeCount, resolveLinkupImage } from '../../../../util/linkupData';
import css from './LinkUpCard.module.css';

import calendarIcon from '../../../../assets/calendar.png';
import locationIcon from '../../../../assets/locationpin.svg';
import groupIcon from '../../../../assets/Group.svg';
import placeholderImage from '../../../../assets/placeholder.png';

const US_STATE_NAME_TO_CODE = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
};

const toStateCode = raw => {
  if (!raw) return '';
  const t = String(raw).trim();
  if (!t) return '';
  if (t.length === 2) return t.toUpperCase();
  return getStateCode(t) || US_STATE_NAME_TO_CODE[t.toLowerCase()] || t;
};

const isCountryToken = v =>
  /^(usa|us|u\.s\.a\.|u\.s\.|united states|united states of america)$/i.test(
    String(v || '').trim()
  );

const extractCityState = linkUp => {
  const city = (linkUp.city || '').trim();
  const state = (linkUp.stateCode || linkUp.state || '').trim();
  if (city && state) return { city, state: toStateCode(state) };

  const address = linkUp.linkUp_address || linkUp.location || '';
  if (typeof address === 'string' && address.includes(',')) {
    const parts = address
      .split(',')
      .map(p => p.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      const co = isCountryToken(parts[parts.length - 1]) ? 1 : 0;
      const si = parts.length - 1 - co;
      const ci = si - 1;
      return {
        city: city || parts[ci] || parts[0],
        state: toStateCode(state || (parts[si] || '').split(/\s+/)[0]),
      };
    }
  }
  return { city, state: toStateCode(state) };
};

const formatLocation = linkUp => {
  const { city, state } = extractCityState(linkUp);
  if (city && state) return `${city}, ${state}`;
  return city || state || linkUp.location || 'Location TBD';
};

const formatDateTime = (dateValue, timeValue) => {
  let dateObj = null;
  if (dateValue) {
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) dateObj = parsed;
  }

  let datePart = '';
  if (dateObj) {
    const wd = dateObj.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    const mo = dateObj.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    datePart = `${wd}, ${mo} ${dateObj.getDate()}`;
  } else if (typeof dateValue === 'string' && dateValue.trim()) {
    datePart = dateValue.trim().toUpperCase();
  } else {
    datePart = 'DATE TBD';
  }

  const timePart = typeof timeValue === 'string' ? timeValue.trim() : '';
  return timePart ? `${datePart} · ${timePart}` : datePart;
};

const preventImageContextMenu = e => e.preventDefault();

const LinkUpCard = ({
  linkUp,
  isJoined = false,
  crewCount,
  onClick = () => {},
  onButtonClick = () => {},
  onCreateClick = () => {},
  theme = 'light',
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const imageSrc = resolveLinkupImage(linkUp);
  const attendeeCount = resolveLinkupAttendeeCount(linkUp);
  const displayImageSrc = !imgFailed && imageSrc ? imageSrc : placeholderImage;
  const description = linkUp.description || '';
  const shouldShowDescriptionToggle = description.length > 150;

  const handleCardClick = () => {
    if (!linkUp.isGeneric) onClick(linkUp.id);
  };

  const handleButtonClick = e => {
    e.stopPropagation();
    onButtonClick(linkUp.id);
  };

  const cardClassName = classNames(css.linkUpCard, {
    [css.going]: isJoined,
    [css.dark]: theme === 'dark',
  });

  if (linkUp.isGeneric) {
    return (
      <div className={classNames(cardClassName, css.genericCard)} style={{ cursor: 'default' }}>
        <div className={css.genericBody}>
          <h3 className={css.genericTitle}>{linkUp.title}</h3>
          <p className={css.genericDesc}>{linkUp.description}</p>
          {linkUp.showCreateCta ? (
            <button type="button" className={css.genericCta} onClick={onCreateClick}>
              Create a LinkUp
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cardClassName}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className={css.imageArea}>
        <img
          src={displayImageSrc}
          alt={linkUp.title}
          className={css.image}
          loading="lazy"
          decoding="async"
          draggable={false}
          onContextMenu={preventImageContextMenu}
          onError={() => setImgFailed(true)}
        />
      </div>

      <div className={css.info}>
        <h3 className={css.cardTitle}>{linkUp.title}</h3>
        <div className={css.row}>
          <SvgIcon src={calendarIcon} alt="" className={css.icon} />
          <span className={css.meta}>{formatDateTime(linkUp.date, linkUp.time)}</span>
        </div>
        <div className={css.row}>
          <SvgIcon src={locationIcon} alt="" className={css.icon} />
          <span className={css.meta}>{formatLocation(linkUp)}</span>
        </div>

        {linkUp.source_crew_name && <p className={css.crewTag}>From: {linkUp.source_crew_name}</p>}

        {description ? (
          <div className={css.descriptionBlock}>
            <p
              className={classNames(css.description, {
                [css.descriptionExpanded]: isDescriptionExpanded,
              })}
            >
              {description}
            </p>
            {shouldShowDescriptionToggle ? (
              <button
                type="button"
                className={css.descriptionToggle}
                onClick={e => {
                  e.stopPropagation();
                  setIsDescriptionExpanded(prev => !prev);
                }}
              >
                {isDescriptionExpanded ? 'Show less' : 'Show more'}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className={css.footer}>
          <div className={css.attendees}>
            <img
              src={groupIcon}
              alt=""
              aria-hidden="true"
              className={css.attendeeIcon}
              draggable={false}
              onContextMenu={preventImageContextMenu}
            />
            <span className={css.attendeeCount}>+{attendeeCount} going</span>
          </div>
          <button type="button" className={css.linkUpCta} onClick={handleButtonClick}>
            {isJoined ? 'Take me there' : 'Go'}
          </button>
        </div>
      </div>
    </div>
  );
};

LinkUpCard.propTypes = {
  linkUp: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    location: PropTypes.string,
    description: PropTypes.string,
    attendees: PropTypes.array,
    totalGoing: PropTypes.number,
    isGeneric: PropTypes.bool,
    showCreateCta: PropTypes.bool,
  }).isRequired,
  isJoined: PropTypes.bool,
  crewCount: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  onButtonClick: PropTypes.func,
  onCreateClick: PropTypes.func,
  theme: PropTypes.oneOf(['light', 'dark']),
};

export default LinkUpCard;
